import { Autocomplete, AutocompleteItem } from '@heroui/autocomplete';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input } from '@heroui/input';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import {
  useAttioControllerBackfillContracts,
  useAttioControllerBackfillSites,
  useAttioControllerTestConnection,
} from '@/api/generated/attio/attio';
import { useBillsControllerRecomputeAllAnalysis } from '@/api/generated/bills/bills';
import {
  useGmailControllerAddWatchedUser,
  useGmailControllerListWatchedUsers,
  useGmailControllerRemoveWatchedUser,
} from '@/api/generated/gmail/gmail';
import type { WatchedUserDto } from '@/api/generated/schemas';
import { useSearchControllerReindex } from '@/api/generated/search/search';
import { useUsersControllerListUsers } from '@/api/generated/users/users';
import { DataTable } from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { InboxRoutesModal } from '@/components/settings/InboxRoutesModal';
import { INBOX_HANDLER_LABELS } from '@/components/settings/inboxHandlerLabels';
import { isEmailAddress } from '@/utils/email';

export const Route = createFileRoute('/settings/')({
  component: Settings,
});

interface ReindexResult {
  sites: number;
  bills: number;
  siteLists: number;
  contracts: number;
}

function Settings() {
  const reindexMutation = useSearchControllerReindex();

  const recomputeAnalysisMutation = useBillsControllerRecomputeAllAnalysis();

  const attioBackfillMutation = useAttioControllerBackfillSites();
  const attioContractBackfillMutation = useAttioControllerBackfillContracts();

  const { data: watchedUsers, refetch: refetchWatchedUsers } = useGmailControllerListWatchedUsers();
  const { data: allUsers } = useUsersControllerListUsers();
  const addWatchedUserMutation = useGmailControllerAddWatchedUser();
  const removeWatchedUserMutation = useGmailControllerRemoveWatchedUser();
  const [newWatchEmail, setNewWatchEmail] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [routesUser, setRoutesUser] = useState<WatchedUserDto | null>(null);

  const watchedEmails = new Set((watchedUsers?.users ?? []).map((u) => u.email));
  const availableUsers = (allUsers?.users ?? [])
    .filter((u) => !watchedEmails.has(u.email))
    .sort((a, b) => a.email.localeCompare(b.email));

  const filteredWatchedUsers = (watchedUsers?.users ?? []).filter(
    (user) => !filterQuery || user.email.toLowerCase().includes(filterQuery.toLowerCase()),
  );

  const canAddWatchedUser = isEmailAddress(newWatchEmail);

  const handleAddWatchedUser = () => {
    if (!canAddWatchedUser) return;
    addWatchedUserMutation.mutate(
      { data: { email: newWatchEmail.trim().toLowerCase() } },
      {
        onSuccess: () => {
          setNewWatchEmail('');
          setShowAddUser(false);
          refetchWatchedUsers();
        },
      },
    );
  };

  const handleRemoveWatchedUser = useCallback(
    (email: string) => {
      removeWatchedUserMutation.mutate({ params: { email } }, { onSuccess: () => refetchWatchedUsers() });
    },
    [removeWatchedUserMutation, refetchWatchedUsers],
  );

  const gmailSyncColumns: DataTableColumn<WatchedUserDto>[] = useMemo(
    () => [
      {
        key: 'email',
        label: 'Email',
        render: (user: WatchedUserDto) => <span className="text-sm">{user.email}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        render: (user: WatchedUserDto) => (
          <span className={`text-sm ${user.enabled ? 'text-success-600' : 'text-default-400'}`}>
            {user.enabled ? 'Active' : 'Disabled'}
          </span>
        ),
      },
      {
        key: 'watchExpiry',
        label: 'Watch Expires',
        render: (user: WatchedUserDto) => (
          <span className="text-sm text-default-500">
            {user.watchExpiry
              ? new Date(user.watchExpiry).toLocaleDateString('en-AU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : '—'}
          </span>
        ),
      },
      {
        key: 'routes',
        label: 'Routes',
        render: (user: WatchedUserDto) => (
          <div className="flex flex-wrap gap-1">
            {user.routes.length === 0 && <span className="text-sm text-default-400">None</span>}
            {user.routes.map((route) => (
              <Chip key={`${route.address}:${route.handler}`} size="sm" variant="flat" color="primary">
                {INBOX_HANDLER_LABELS[route.handler]}
                {route.address !== user.email ? ` · ${route.address}` : ''}
              </Chip>
            ))}
          </div>
        ),
      },
      {
        key: 'actions',
        label: '',
        align: 'end' as const,
        render: (user: WatchedUserDto) => (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="light" onPress={() => setRoutesUser(user)}>
              Routes
            </Button>
            <Button color="danger" size="sm" variant="light" onPress={() => handleRemoveWatchedUser(user.email)}>
              Remove
            </Button>
          </div>
        ),
      },
    ],
    [handleRemoveWatchedUser],
  );

  const [attioTestTriggered, setAttioTestTriggered] = useState(false);
  const {
    data: attioTestResult,
    isLoading: isAttioTesting,
    refetch: refetchAttioTest,
  } = useAttioControllerTestConnection({
    query: { enabled: attioTestTriggered },
  });

  const [confirmRecompute, setConfirmRecompute] = useState(false);

  const handleReindex = () => {
    reindexMutation.mutate();
  };

  const handleRecomputeAnalysis = () => {
    setConfirmRecompute(false);
    recomputeAnalysisMutation.mutate();
  };

  const handleTestAttioConnection = () => {
    setAttioTestTriggered(true);
    refetchAttioTest();
  };

  const reindexResult = reindexMutation.data as ReindexResult | undefined;

  return (
    <div className="py-6">
      <div className="flex flex-col gap-6">
        <PageHeader title="Settings" description="Manage application settings and integrations." />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-5">
              <h3 className="text-lg font-semibold">Search Index</h3>
              <p className="text-sm text-default-500">
                Rebuild the search index to ensure all entities are up to date in search results.
              </p>
            </CardHeader>
            <Divider />
            <CardBody className="px-6 py-5 gap-3">
              <Button color="primary" isLoading={reindexMutation.isPending} onPress={handleReindex}>
                Rebuild Search Index
              </Button>
              {reindexMutation.isSuccess && reindexResult && (
                <div className="rounded-md bg-success-50 p-3 text-success-800 text-sm">
                  <p className="font-medium">Reindex complete</p>
                  <p className="mt-1">
                    {reindexResult.sites} sites, {reindexResult.bills} bills, {reindexResult.siteLists} site lists,{' '}
                    {reindexResult.contracts} contracts
                  </p>
                </div>
              )}
              {reindexMutation.isError && (
                <div className="rounded-md bg-danger-50 p-3 text-danger-800 text-sm">
                  <p className="font-medium">Reindex failed</p>
                  <p className="mt-1">An error occurred while rebuilding the search index. Please try again.</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-5">
              <h3 className="text-lg font-semibold">Bill Analysis</h3>
              <p className="text-sm text-default-500">
                Re-derive tariff classification and usage totals for every bill from its already-extracted charges, then
                resync the affected sites. Repairs bills whose usage reads as zero. Runs in the background and does not
                re-read the source PDFs.
              </p>
            </CardHeader>
            <Divider />
            <CardBody className="px-6 py-5 gap-3">
              {!confirmRecompute ? (
                <Button
                  color="primary"
                  isLoading={recomputeAnalysisMutation.isPending}
                  onPress={() => setConfirmRecompute(true)}
                >
                  Recompute All Bill Analysis
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-default-600">This rewrites the stored analysis on every bill. Continue?</p>
                  <div className="flex gap-2">
                    <Button color="primary" onPress={handleRecomputeAnalysis}>
                      Confirm
                    </Button>
                    <Button variant="light" onPress={() => setConfirmRecompute(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {recomputeAnalysisMutation.isSuccess && (
                <div className="rounded-md bg-success-50 p-3 text-success-800 text-sm">
                  <p className="font-medium">Recompute queued</p>
                  <p className="mt-1">
                    {recomputeAnalysisMutation.data.billsQueued} bills across{' '}
                    {recomputeAnalysisMutation.data.tasksQueued} background tasks. Site usage updates as each task
                    finishes.
                  </p>
                </div>
              )}
              {recomputeAnalysisMutation.isError && (
                <div className="rounded-md bg-danger-50 p-3 text-danger-800 text-sm">
                  <p className="font-medium">Failed to queue recompute</p>
                  <p className="mt-1">An error occurred while queueing the recompute. Please try again.</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-5">
              <h3 className="text-lg font-semibold">Attio Integration</h3>
              <p className="text-sm text-default-500">Manage the Attio CRM integration.</p>
            </CardHeader>
            <Divider />
            <CardBody className="px-6 py-5 gap-3">
              <Button color="primary" isLoading={isAttioTesting} onPress={handleTestAttioConnection}>
                Test Connection
              </Button>
              {attioTestResult && (
                <div
                  className={`rounded-md p-3 text-sm ${
                    attioTestResult.success ? 'bg-success-50 text-success-800' : 'bg-danger-50 text-danger-800'
                  }`}
                >
                  {attioTestResult.success ? (
                    <div>
                      <p className="font-medium">Connection successful</p>
                      {attioTestResult.workspaceId && <p className="mt-1">Workspace: {attioTestResult.workspaceId}</p>}
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">Connection failed</p>
                      {attioTestResult.error && <p className="mt-1">{attioTestResult.error}</p>}
                    </div>
                  )}
                </div>
              )}
              <Divider />
              <Button
                color="secondary"
                isLoading={attioBackfillMutation.isPending}
                onPress={() => attioBackfillMutation.mutate()}
              >
                Backfill All Sites
              </Button>
              {attioBackfillMutation.isSuccess && (
                <div className="rounded-md bg-success-50 p-3 text-success-800 text-sm">
                  <p className="font-medium">Backfill enqueued</p>
                  <p className="mt-1">{attioBackfillMutation.data.enqueuedCount} sites queued for sync</p>
                </div>
              )}
              {attioBackfillMutation.isError && (
                <div className="rounded-md bg-danger-50 p-3 text-danger-800 text-sm">
                  <p className="font-medium">Backfill failed</p>
                  <p className="mt-1">An error occurred while enqueueing sites. Please try again.</p>
                </div>
              )}
              <Divider />
              <Button
                color="secondary"
                isLoading={attioContractBackfillMutation.isPending}
                onPress={() => attioContractBackfillMutation.mutate()}
              >
                Backfill All Contracts
              </Button>
              {attioContractBackfillMutation.isSuccess && (
                <div className="rounded-md bg-success-50 p-3 text-success-800 text-sm">
                  <p className="font-medium">Backfill enqueued</p>
                  <p className="mt-1">{attioContractBackfillMutation.data.enqueuedCount} contracts queued for sync</p>
                </div>
              )}
              {attioContractBackfillMutation.isError && (
                <div className="rounded-md bg-danger-50 p-3 text-danger-800 text-sm">
                  <p className="font-medium">Backfill failed</p>
                  <p className="mt-1">An error occurred while enqueueing contracts. Please try again.</p>
                </div>
              )}
            </CardBody>
          </Card>

          <Card className="sm:col-span-2">
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-5">
              <div className="flex items-center justify-between w-full">
                <div>
                  <h3 className="text-lg font-semibold">Gmail Sync</h3>
                  <p className="text-sm text-default-500">
                    Monitored inboxes for NMI-matched emails. Users are added automatically on sign-up. Routes hand mail
                    for an address to a handler, such as bill ingest for the SME Tenders mailbox.
                  </p>
                </div>
                {!showAddUser ? (
                  <Button color="primary" size="sm" onPress={() => setShowAddUser(true)}>
                    Add User
                  </Button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Autocomplete
                      autoFocus
                      allowsCustomValue
                      placeholder="User or shared mailbox..."
                      size="sm"
                      className="w-64"
                      inputValue={newWatchEmail}
                      onInputChange={setNewWatchEmail}
                      onSelectionChange={(key) => {
                        if (key != null) setNewWatchEmail(key as string);
                      }}
                    >
                      {availableUsers.map((user) => (
                        <AutocompleteItem key={user.email}>{user.email}</AutocompleteItem>
                      ))}
                    </Autocomplete>
                    <Button
                      color="primary"
                      size="sm"
                      isDisabled={!canAddWatchedUser}
                      isLoading={addWatchedUserMutation.isPending}
                      onPress={handleAddWatchedUser}
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => {
                        setShowAddUser(false);
                        setNewWatchEmail('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              {addWatchedUserMutation.isError && (
                <div className="rounded-md bg-danger-50 p-3 text-danger-800 text-sm w-full">
                  <p className="font-medium">Failed to add user</p>
                </div>
              )}
            </CardHeader>
            <Divider />
            <CardBody className="px-6 py-5 gap-3">
              <Input
                placeholder="Filter..."
                size="sm"
                className="max-w-[200px]"
                value={filterQuery}
                onValueChange={setFilterQuery}
              />
              <div className="max-h-80 overflow-y-auto -mx-6">
                <DataTable
                  columns={gmailSyncColumns}
                  data={filteredWatchedUsers}
                  getRowKey={(user) => user.email}
                  emptyContent={
                    <p className="text-sm text-default-400">
                      {watchedUsers?.users?.length
                        ? 'No users match your filter.'
                        : 'No users are being monitored yet.'}
                    </p>
                  }
                  aria-label="Gmail watched users"
                />
              </div>
            </CardBody>
          </Card>
          <InboxRoutesModal
            user={routesUser}
            onClose={() => setRoutesUser(null)}
            onSaved={() => {
              setRoutesUser(null);
              refetchWatchedUsers();
            }}
          />
        </div>
      </div>
    </div>
  );
}
